import { DateTime } from 'luxon';
export const getLondonDateTime = (date) => DateTime.fromObject({
    year: date.year,
    month: date.month,
    day: date.day,
}).setZone('Europe/London');
export const getWeeksInMonth = (date) => {
    var _a;
    const weeks = [...Array(6)].map(() => []);
    const startDay = date.startOf('month').weekday;
    const endDay = date.endOf('month').weekday;
    // Add preceding days
    if (startDay > 1) {
        const previousMonth = date.minus({ months: 1 });
        for (let i = startDay - 1; i > 0; i--) {
            weeks[0].push({
                dateTime: DateTime.fromObject({
                    year: previousMonth.year,
                    month: previousMonth.month,
                    day: previousMonth.daysInMonth - (i - 1),
                }),
                displayable: false,
                dayPosition: 'preceding',
            });
        }
    }
    // Add days of the month
    [...Array(date.daysInMonth)].forEach((_, i) => {
        var _a;
        return (_a = weeks
            .find((week) => week.length < 7)) === null || _a === void 0 ? void 0 : _a.push({
            dateTime: DateTime.fromObject({ year: date.year, month: date.month, day: i + 1 }),
            displayable: true,
            dayPosition: 'current',
        });
    });
    // Add following days
    const remainingDayNum = 7 - endDay;
    for (let i = 1; i <= remainingDayNum; i++) {
        (_a = weeks
            .find((week) => week.length < 7)) === null || _a === void 0 ? void 0 : _a.push({
            dateTime: DateTime.fromObject({
                year: date.plus({ months: 1 }).year,
                month: date.plus({ months: 1 }).month,
                day: i,
            }),
            displayable: false,
            dayPosition: 'following',
        });
    }
    return weeks.filter((week) => week.length);
};
export const isFollowing = (day) => day.dayPosition === 'following';
export const isPreceding = (day) => day.dayPosition === 'preceding';
export const isSameDate = (dateX, dateY) => {
    if (!dateX || !dateY)
        return false;
    return dateX.toISO() === dateY.toISO();
};
export const isFirstDayOfMonth = (date) => date.startOf('month').toISODate() === date.toISODate();
export const isPast = (date) => date.startOf('day').toISO() < DateTime.now().startOf('day').toISO();
export const isPastMonth = (date) => date.startOf('month').toISO() < DateTime.now().startOf('month').toISO();
export const isToday = (date) => isSameDate(date.startOf('day'), DateTime.now().startOf('day'));
export const isSelected = (date, selection) => isSameDate(date, selection.startDate) || isSameDate(date, selection.endDate);
export const isStartOfRange = (date, selection) => isSameDate(date, selection.startDate);
export const isInRange = (date, selection, closedInterval = false) => {
    if (!selection.startDate || !selection.endDate)
        return false;
    return closedInterval
        ? selection.startDate <= date && date <= selection.endDate
        : selection.startDate < date && date < selection.endDate;
};
export const isEndOfRange = (date, selection) => {
    if (!selection.endDate)
        return false;
    return isSameDate(date, selection.endDate);
};
export const isInHiddenSelectionRange = (day, selection) => {
    if (!selection.startDate || !selection.endDate)
        return false;
    if (selection.startDate.hasSame(selection.endDate, 'month')) {
        return false;
    }
    // for multiple months selection
    if (isInRange(day.dateTime, selection)) {
        if (isFollowing(day)) {
            return !day.dateTime.hasSame(selection.startDate, 'month');
        }
        if (isPreceding(day)) {
            return !day.dateTime.hasSame(selection.endDate, 'month');
        }
        return true;
    }
    // if the selection goes through exactly two months
    if (isFollowing(day)) {
        return day.dateTime.hasSame(selection.endDate, 'month');
    }
    if (isPreceding(day)) {
        return day.dateTime.hasSame(selection.startDate, 'month');
    }
    return false;
};
export const isHiddenEventDay = (day, eventProps) => day.dayPosition !== eventProps.dayPosition;
// some event is only able to exist on slot0
export const isOverwrittenEvent = (eventProps) => {
    if (eventProps.slot > 0 && eventProps.styleType === 'dashed') {
        return true;
    }
    return false;
};
const getBaseStyle = (data) => {
    const styleMap = {};
    if (data.baseClasses) {
        styleMap.start = data.baseClasses.start;
        styleMap.part = data.baseClasses.part;
        styleMap.end = data.baseClasses.end;
        return styleMap;
    }
    // event is one day long with image
    if (data.styleType === 'image') {
        styleMap.single = 'eventImage';
        return styleMap;
    }
    // event is one day long thus single day class needed
    if (data.styleType === 'normal' && data.days.length === 1) {
        styleMap.single = 'normalEventSingle';
        return styleMap;
    }
    if (data.styleType === 'dashed' && data.days.length === 1) {
        styleMap.single = 'dashedEventSingle';
        return styleMap;
    }
    if (data.styleType === 'normal') {
        styleMap.start = 'normalEventStart';
        styleMap.part = 'normalEventPart';
        styleMap.end = 'normalEventEnd';
        return styleMap;
    }
    if (data.styleType === 'partEx') {
        styleMap.start = 'partExEventStart';
        styleMap.part = 'partExEventPart';
        styleMap.end = 'partExEventEnd';
        return styleMap;
    }
    if (data.styleType === 'dashed') {
        styleMap.start = 'dashedEventStart';
        styleMap.part = 'dashedEventPart';
        styleMap.end = 'dashedEventEnd';
        return styleMap;
    }
    return styleMap;
};
const eventSorter = (a, b) => {
    // if order is present
    // defined order > undefined order
    if (a.order && !b.order) {
        return -1;
    }
    if (!a.order && b.order) {
        return 1;
    }
    if (a.order && b.order) {
        // smaller order value should be rendered first
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 1;
        }
    }
    // if orders are equals then
    // Longer events rendered first
    if (a.days.length > b.days.length) {
        return -1;
    }
    if (a.days.length < b.days.length) {
        return 1;
    }
    // if length is the same choose the first in time
    const aDateTime = a.days.find((day) => day.eventPosition === 'start' || day.eventPosition === 'single');
    const bDateTime = b.days.find((day) => day.eventPosition === 'start' || day.eventPosition === 'single');
    if (a.days.length === b.days.length && aDateTime && bDateTime) {
        return aDateTime > bDateTime ? 1 : -1;
    }
    return 0;
};
export const getDayEventPropsMap = (eventData) => {
    const dayEventPropsMap = {};
    eventData.sort(eventSorter).forEach((data) => {
        const styleMap = getBaseStyle(data);
        if (data.styleType === 'custom' && !data.onRenderCustomEvent) {
            // eslint-disable-next-line no-console
            console.error('Please provide a component for the custom event!');
            return;
        }
        // init keys for prop determination and
        // determine slot for event days
        let startSlot = 0;
        let endSlot = 0;
        let partSlot = 0;
        let afterStartSlot = 0;
        let beforeEndSlot = 0;
        let slot = 0;
        data.days.forEach((day, i) => {
            // init
            const mapKey = day.date.toFormat('yyyy-MM-dd');
            if (!dayEventPropsMap[mapKey]) {
                dayEventPropsMap[mapKey] = {
                    dayStyles: {},
                    events: [],
                    inBetweenEvents: [],
                };
            }
            const { events } = dayEventPropsMap[mapKey];
            // start slot of the event
            if (day.eventPosition === 'start') {
                startSlot = events.length;
            }
            // end slot of the event
            if (day.eventPosition === 'end') {
                endSlot = events.length;
            }
            // one day long event
            if (day.eventPosition === 'single') {
                startSlot = events.length;
                endSlot = events.length;
            }
            if (day.eventPosition === 'part') {
                partSlot = events.length;
            }
            // if the part after start or the part before overlaps then move to the next slot
            if (day.eventPosition === 'part' && events.length > 0) {
                if (data.days[i - 1].eventPosition === 'start') {
                    afterStartSlot = events.length;
                }
                if (data.days[i + 1].eventPosition === 'end') {
                    beforeEndSlot = events.length;
                }
            }
        });
        // if start slot and end slot are on higher slot but part slots are free
        if (partSlot === 0) {
            slot = 0;
        }
        // if start and end position is below slot 0 then the event is hidden (-1)
        if (startSlot === endSlot && startSlot > 0 && partSlot != 0) {
            slot = -1;
        }
        // start event position and end event position are on different slot - adjust the whole event
        if (startSlot !== endSlot) {
            // move whole event to the largest slot position
            if (afterStartSlot > 0 || beforeEndSlot > 0) {
                slot = startSlot > endSlot ? startSlot : endSlot;
            }
            else {
                slot = startSlot < endSlot ? startSlot : endSlot;
            }
            // only slot0 and slot1 supported - other slots are considered hidden
            slot = slot > 1 ? -1 : slot;
        }
        // determine props for the CalendarEvent component
        data.days.forEach((day) => {
            var _a;
            const mapKey = day.date.toFormat('yyyy-MM-dd');
            const { events, inBetweenEvents, dayStyles } = dayEventPropsMap[mapKey];
            // currently events with day specific styles cannot overlap - so overwrite not allowed
            // dayStylesOnly events should also not override styling for past dates
            if (data.dayStyles &&
                (data.styleType !== 'dayStylesOnly' || !isPast(day.date)) &&
                !Object.keys(dayStyles).length) {
                dayEventPropsMap[mapKey].dayStyles = data.dayStyles;
            }
            const arrivalBookingCode = 999;
            const generateSlot = (existingSlot) => {
                if (data.code === arrivalBookingCode) {
                    if (events === null || events === void 0 ? void 0 : events.find((event) => event.code === arrivalBookingCode)) {
                        return -1;
                    }
                    return 1;
                }
                return existingSlot;
            };
            // create the CalendarEvent props
            const eventData = {
                slot: generateSlot(slot),
                id: data.id,
                styleType: data.styleType,
                code: data.code,
                value: day.eventPosition === 'start' || day.eventPosition === 'single' ? data.value : undefined,
                label: day.eventPosition === 'start' || day.eventPosition === 'single' ? data.label : undefined,
                eventPosition: day.eventPosition,
                dayPosition: day.dayPosition,
                baseStyle: styleMap[day.eventPosition],
                bannerStyles: (_a = data.bannerStyles) !== null && _a !== void 0 ? _a : {},
                onRenderCustomEvent: data.onRenderCustomEvent,
            };
            if (day.dayPosition === 'current') {
                events.push(eventData);
            }
            else {
                inBetweenEvents.push(eventData);
            }
        });
    });
    return dayEventPropsMap;
};
// create DayEvent[] from two DateTime
export const getEventDays = (startDateTime, endDateTime) => {
    const days = [];
    // set correct start and end dateTime
    const startDate = startDateTime.startOf('day');
    const endDate = endDateTime.startOf('day');
    // one day long event
    if (isSameDate(startDate, endDate)) {
        days.push({
            eventPosition: 'single',
            date: startDate,
            dayPosition: 'current',
        });
        return days;
    }
    // determine the position of the events
    for (let dateStep = startDate; dateStep <= endDate; dateStep = dateStep.plus({ days: 1 })) {
        const nextStep = dateStep.plus({ days: 1 });
        const isStartPart = isSameDate(dateStep, startDate);
        const isConnectorPart = nextStep <= endDate && nextStep.month !== dateStep.month;
        const isInBetweenPart = dateStep < endDate;
        const isEndPart = isSameDate(dateStep, endDate);
        if (isStartPart) {
            days.push({
                eventPosition: 'start',
                date: dateStep,
                dayPosition: 'current',
            });
        }
        // preceding and following event parts if the event goes trough multiple months
        if (isConnectorPart) {
            // add following days for prev month
            const startDay = dateStep.endOf('month').weekday;
            const remainingDayNum = 7 - startDay;
            for (let i = 1; i <= remainingDayNum; i++) {
                days.push({
                    date: DateTime.fromObject({
                        year: nextStep.year,
                        month: nextStep.month,
                        day: i,
                    }),
                    eventPosition: 'part',
                    dayPosition: 'following',
                });
            }
            // add preceding days of next month
            const endDay = nextStep.startOf('month').weekday;
            if (endDay > 1) {
                for (let i = endDay - 1; i > 0; i--) {
                    days.push({
                        date: DateTime.fromObject({
                            year: dateStep.year,
                            month: dateStep.month,
                            day: dateStep.daysInMonth - (i - 1),
                        }),
                        eventPosition: 'part',
                        dayPosition: 'preceding',
                    });
                }
            }
        }
        if (!isStartPart && isInBetweenPart) {
            days.push({
                eventPosition: 'part',
                date: dateStep,
                dayPosition: 'current',
            });
        }
        if (isEndPart) {
            days.push({
                eventPosition: 'end',
                date: dateStep,
                dayPosition: 'current',
            });
        }
    }
    return days;
};
export const getEventDaysWithoutConnector = (startDateTime, endDateTime) => {
    const days = [];
    // set correct start and end dateTime
    const startDate = startDateTime.startOf('day');
    const endDate = endDateTime.startOf('day');
    // one day long event
    if (isSameDate(startDate, endDate)) {
        days.push({
            eventPosition: 'single',
            date: startDate,
            dayPosition: 'current',
        });
        return days;
    }
    // determine the position of the events
    for (let dateStep = startDate; dateStep <= endDate; dateStep = dateStep.plus({ days: 1 })) {
        const isStartPart = isSameDate(dateStep, startDate);
        const isInBetweenPart = dateStep < endDate;
        const isEndPart = isSameDate(dateStep, endDate);
        if (isStartPart) {
            days.push({
                eventPosition: 'start',
                date: dateStep,
                dayPosition: 'current',
            });
        }
        if (!isStartPart && isInBetweenPart) {
            days.push({
                eventPosition: 'part',
                date: dateStep,
                dayPosition: 'current',
            });
        }
        if (isEndPart) {
            days.push({
                eventPosition: 'end',
                date: dateStep,
                dayPosition: 'current',
            });
        }
    }
    return days;
};
export const getEventIds = (selection, eventData) => {
    // group events by id
    const dayEventsIdMap = eventData.reduce((results, data) => {
        (results[data.id] = results[data.id] || []).push(...data.days);
        return results;
    }, {});
    const eventIds = new Set();
    Object.keys(dayEventsIdMap).forEach((id) => {
        dayEventsIdMap[id].forEach((dayEvent) => {
            if (!isPreceding(dayEvent) &&
                !isFollowing(dayEvent) &&
                isInRange(dayEvent.date, selection, true)) {
                eventIds.add(id);
                return;
            }
        });
    });
    return eventIds;
};
export const getHiddenEventCount = (calendarEventProps) => calendarEventProps.filter((prop) => prop.slot === -1 && prop.styleType !== 'dayStylesOnly' && prop.styleType !== 'dashed').length;
export const isSelectable = (date) => DateTime.now().startOf('day') <= date;
export const hasPastDays = (weeks) => weeks.some((week) => week.some((day) => isPast(day.dateTime) && day.displayable));
export const displayAsPast = (day) => {
    const nextMonth = day.dateTime.plus({ months: 1 });
    const pastMonth = day.dateTime.minus({ months: 1 });
    return ((isPreceding(day) && isPast(nextMonth.startOf('month'))) ||
        (isFollowing(day) && isPast(pastMonth.endOf('month'))));
};
export const getInitialDate = (date) => {
    if (!date)
        return DateTime.now();
    const initialDate = DateTime.fromISO(date);
    return initialDate.isValid ? initialDate : DateTime.now();
};
